<script setup>
import { useRuntimeConfig } from '#imports';
import { onMounted, ref, computed } from 'vue';
import { useAuth } from '@/composables/use-auth';
import { usePersonalDetails } from '@/composables/use-personal-details';
import { useRouter } from 'vue-router';
import { useNotifsTailed, useNotifs } from '@/composables/use-notifs';
import { useToast } from '@/composables/use-ui';
import GlobalToast from '@/components/commons/GlobalToast.vue';

const router = useRouter();
const { showToast } = useToast();


useNotifsTailed({
  onNewNotif: (notif) => {
    showToast(notif.title, { type: 'alert-info' });
    // Refresh unread count when new notification arrives
    fetchUnreadCount();
  },
});
const { currentUser, signout } = useAuth();
const { getItem: getPersonalDetails } = usePersonalDetails();
const { fetchNotifsCount } = useNotifs();

const personalDetails = ref({});
const email = computed(() => currentUser.value?.email);
const picURL = computed(() => personalDetails.value?.picURL);
const unreadNotifCount = ref(0);

// Function to fetch unread notification count
const fetchUnreadCount = async () => {
  try {
    unreadNotifCount.value = await fetchNotifsCount({
      filters: {
        seenBy: {
          $ne: currentUser.value?.uid,
        },
      }
    });

    console.log('fetchUnreadCount > unreadNotifCount', unreadNotifCount.value);
  } catch (error) {
    console.error('Failed to fetch notification count:', error);
  }
};

onMounted(async () => {
  if (!currentUser.value) {
    signout();
    router.push({ name: 'login' });
  }
  
  personalDetails.value = await getPersonalDetails(currentUser.value?.uid);
  
  // Fetch initial unread notification count
  await fetchUnreadCount();

  console.log('unreadNotifCount', unreadNotifCount.value);
});

async function onSignOut () {
  await signout();
  router.push({ name: 'login' });
}

const appVersion = useRuntimeConfig().public?.appVersion;

const drawer = ref(false);
const sidebarCollapsed = ref(false);

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
};
</script>

<template>
  <div data-theme="parmazip">
    <div class="navbar justify-between bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-50 shadow-sm">
      <div>
        <label for="nav-drawer" class="btn btn-sm btn-ghost btn-square mr-2 drawer-button lg:hidden text-gray-600 hover:text-gray-900 hover:bg-gray-100">
          <i class="las la-bars text-xl"/>
        </label>
        <div class="flex items-center gap-2">
          <nuxt-link href="https://parmazip.ca" target="_blank" rel="noopener noreferrer" class="flex-1">
            <img src="@/assets/images/parmazip-logo-long.png" class="h-[60px]" alt="Parmazip Logo">
          </nuxt-link>
        </div>
      </div>
      <div class="flex-none flex items-center gap-2">
        <!-- Notifications Button -->
        <nuxt-link 
          to="/dashboard/patient-notifs" 
          class="btn btn-ghost btn-circle hover:bg-gray-100 relative"
          @click="fetchUnreadCount"
        >
          <i class="las la-bell text-xl text-gray-600"/>
          <!-- Unread notification badge -->
          <span 
            v-if="unreadNotifCount > 0"
            class="absolute top-2 right-2 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
          >
            {{ unreadNotifCount > 99 ? '99+' : unreadNotifCount }}
          </span>
        </nuxt-link>
        
        <!-- User Profile Dropdown -->
        <div class="dropdown dropdown-end">
          <div tabindex="0" role="button" class="btn btn-ghost btn-circle avatar hover:bg-gray-100">
            <div class="w-10 rounded-full bg-gray-100 border border-gray-200">
              <img
                v-if="picURL"
                :alt="email"
                :src="picURL"
              >
              <i v-else class="las la-user text-xl mt-1 text-gray-600"/>
            </div>
          </div>
          <ul
            tabindex="0"
            class="menu menu-sm dropdown-content bg-white rounded-box z-[1] mt-3 w-52 p-2 shadow-md border border-gray-200">
            <li>
              <nuxt-link to="/dashboard/patient-profile" class="hover:bg-gray-100 hover:text-gray-900">
                <i class="las la-user-circle text-lg mr-2"/>
                Profile
              </nuxt-link>
            </li>
            <li>
              <nuxt-link class="hover:bg-gray-100 hover:text-gray-900" @click="onSignOut">
                <i class="las la-sign-out-alt text-lg mr-2"/>
                Log Out
              </nuxt-link>
            </li>
            <li class="disabled mt-2">
              <span class="text-xs text-gray-500">App Version: {{ appVersion }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="drawer lg:drawer-open pt-[72px]">
      <input id="nav-drawer" v-model="drawer" type="checkbox" class="drawer-toggle" >
      <div class="drawer-content">
        <slot/>
      </div>
      <div class="drawer-side z-40">
        <label for="nav-drawer" aria-label="close sidebar" class="drawer-overlay"/>
        <div 
          class="bg-white min-h-full pt-[72px] lg:pt-4 shadow-sm border-r border-gray-200 transition-all duration-300"
          :class="sidebarCollapsed ? 'w-16 lg:w-20' : 'w-64 lg:w-80'"
        >
          <!-- User Profile Section -->
          <div v-if="!sidebarCollapsed" class="px-6 py-4 border-b border-gray-200">
            <div class="flex items-center gap-3">
              <div class="w-12 h-12 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center">
                <img
                  v-if="picURL"
                  :alt="email"
                  :src="picURL"
                  class="w-12 h-12 rounded-xl object-cover"
                >
                <i v-else class="las la-user text-xl text-gray-600"/>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-gray-900 truncate">
                  {{ personalDetails?.name?.firstName || 'Patient' }} {{ personalDetails?.name?.lastName || '' }}
                </div>
                <div class="text-xs text-gray-500 truncate">{{ email }}</div>
              </div>
              <!-- Toggle Button -->
              <button 
                class="hidden lg:block p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 flex-shrink-0"
                :title="'Collapse sidebar'"
                @click="toggleSidebar"
              >
                <i class="las la-angle-left text-gray-600 text-lg"/>
              </button>
            </div>
          </div>

          <!-- Collapsed User Avatar -->
          <div v-if="sidebarCollapsed" class="flex justify-center py-4 border-b border-gray-200 relative">
            <div class="w-10 h-10 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center">
              <img
                v-if="picURL"
                :alt="email"
                :src="picURL"
                class="w-10 h-10 rounded-xl object-cover"
              >
              <i v-else class="las la-user text-lg text-gray-600"/>
            </div>
            <!-- Toggle Button for Collapsed State -->
            <button 
              class="hidden lg:block absolute top-2 right-2 p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              :title="'Expand sidebar'"
              @click="toggleSidebar"
            >
              <i class="las la-angle-right text-gray-600 text-lg"/>
            </button>
          </div>

          <!-- Navigation Menu -->
          <nav :class="sidebarCollapsed ? 'px-2 py-6' : 'px-4 py-6'">
            <div class="space-y-2">
              <nuxt-link 
                to="/dashboard/patient" 
                class="flex items-center text-gray-700 rounded-xl hover:bg-red-50 hover:text-primary transition-all duration-200 group"
                :class="[
                  sidebarCollapsed ? 'justify-center p-3' : 'gap-3 px-4 py-3',
                  $route.path === '/dashboard/patient' ? 'bg-red-50 text-primary' : ''
                ]"
                :title="sidebarCollapsed ? 'Dashboard' : ''"
                @click="drawer = false"
              >
                <div
                  class="flex items-center justify-center transition-all duration-200 flex-shrink-0"
                  :class="$route.path === '/dashboard/patient' ? 'w-10 h-10' : 'w-8 h-8'"
                >
                  <i 
                    class="las la-home text-xl transition-colors" 
                    :class="$route.path === '/dashboard/patient' 
                      ? 'text-primary' 
                      : 'text-gray-500 group-hover:text-primary'"
                  />
                </div>
                <span v-if="!sidebarCollapsed" class="font-open-sans font-medium">Dashboard</span>
              </nuxt-link>

              <nuxt-link 
                to="/dashboard/patient-profile" 
                class="flex items-center text-gray-700 rounded-xl hover:bg-red-50 hover:text-primary transition-all duration-200 group"
                :class="[
                  sidebarCollapsed ? 'justify-center p-3' : 'gap-3 px-4 py-3',
                  $route.path === '/dashboard/patient-profile' ? 'bg-red-50 text-primary' : ''
                ]"
                :title="sidebarCollapsed ? 'Profile' : ''"
                @click="drawer = false"
              >
                <div
                  class="flex items-center justify-center transition-all duration-200 flex-shrink-0"
                  :class="$route.path === '/dashboard/patient-profile' ? 'w-10 h-10' : 'w-8 h-8'"
                >
                  <i 
                    class="las la-user text-xl transition-colors" 
                    :class="$route.path === '/dashboard/patient-profile' 
                      ? 'text-primary' 
                      : 'text-gray-500 group-hover:text-primary'"
                  />
                </div>
                <span v-if="!sidebarCollapsed" class="font-open-sans font-medium">Profile</span>
              </nuxt-link>

              <nuxt-link 
                to="/dashboard/patient-appointments" 
                class="flex items-center text-gray-700 rounded-xl hover:bg-red-50 hover:text-primary transition-all duration-200 group"
                :class="[
                  sidebarCollapsed ? 'justify-center p-3' : 'gap-3 px-4 py-3',
                  $route.path === '/dashboard/patient-appointments' ? 'bg-red-50 text-primary' : ''
                ]"
                :title="sidebarCollapsed ? 'My Appointments' : ''"
                @click="drawer = false"
              >
                <div
                  class="flex items-center justify-center transition-all duration-200 flex-shrink-0"
                  :class="$route.path === '/dashboard/patient-appointments' ? 'w-10 h-10' : 'w-8 h-8'"
                >
                  <i 
                    class="las la-calendar text-xl transition-colors" 
                    :class="$route.path === '/dashboard/patient-appointments' 
                      ? 'text-primary' 
                      : 'text-gray-500 group-hover:text-primary'"
                  />
                </div>
                <span v-if="!sidebarCollapsed" class="font-open-sans font-medium">My Appointments</span>
              </nuxt-link>

              <nuxt-link 
                to="/dashboard/patient-billing" 
                class="flex items-center text-gray-700 rounded-xl hover:bg-red-50 hover:text-primary transition-all duration-200 group"
                :class="[
                  sidebarCollapsed ? 'justify-center p-3' : 'gap-3 px-4 py-3',
                  $route.path === '/dashboard/patient-billing' ? 'bg-red-50 text-primary' : ''
                ]"
                :title="sidebarCollapsed ? 'Billing' : ''"
                @click="drawer = false"
              >
                <div
                  class="flex items-center justify-center transition-all duration-200 flex-shrink-0"
                  :class="$route.path === '/dashboard/patient-billing' ? 'w-10 h-10' : 'w-8 h-8'"
                >
                  <i 
                    class="las la-credit-card text-xl transition-colors" 
                    :class="$route.path === '/dashboard/patient-billing' 
                      ? 'text-primary' 
                      : 'text-gray-500 group-hover:text-primary'"
                  />
                </div>
                <span v-if="!sidebarCollapsed" class="font-open-sans font-medium">Billing</span>
              </nuxt-link>

              <nuxt-link 
                to="/dashboard/patient-records" 
                class="flex items-center text-gray-700 rounded-xl hover:bg-red-50 hover:text-primary transition-all duration-200 group"
                :class="[
                  sidebarCollapsed ? 'justify-center p-3' : 'gap-3 px-4 py-3',
                  $route.path === '/dashboard/patient-records' ? 'bg-red-50 text-primary' : ''
                ]"
                :title="sidebarCollapsed ? 'Records' : ''"
                @click="drawer = false"
              >
                <div
                  class="flex items-center justify-center transition-all duration-200 flex-shrink-0"
                  :class="$route.path === '/dashboard/patient-records' ? 'w-10 h-10' : 'w-8 h-8'"
                >
                  <i 
                    class="las la-briefcase-medical text-xl transition-colors" 
                    :class="$route.path === '/dashboard/patient-records' 
                      ? 'text-primary' 
                      : 'text-gray-500 group-hover:text-primary'"
                  />
                </div>
                <span v-if="!sidebarCollapsed" class="font-open-sans font-medium">Records</span>
              </nuxt-link>
            </div>
          </nav>

          <!-- Footer Section -->
          <div v-if="!sidebarCollapsed" class="absolute bottom-4 left-4 right-4">
            <div class="bg-gray-50 rounded-xl p-3 border border-gray-200">
              <div class="text-xs text-gray-500 text-center">
                App Version: {{ appVersion }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <GlobalToast/>
</template>
